/**
 * Horizontal nested menu.
 */
(() => {
    function menu_nested_horizontal(args) {
        let doc = document.documentElement;
        let menu = args.menu;
        let hoverTimeouts = [];

        /**
         * This disables 2nd level sub menus from reacting to mouse hovers
         * when the 1st level sub menu doesn't fit the screen.
         */
        const disableHoversWhenParentOutOfBounds = true;

        if (!menu) {
            return;
        }

        if (!H_Tools?.nestedmenu) {
            return;
        }

        /**
         * Deactivate all <li> in all horizontal nested menus that are not ancestors of the specified
         * menu item.
         *
         * @param {*} li A menu item
         */
        function deactivateOtherItems(li) {
            const ancestors = H_Tools.ancestors.getAllAncestors(
                li,
                menu,
                'menu-item-has-children'
            );

            document
                .querySelectorAll(
                    '.js-menu-nested-horizontal .menu-item-has-children'
                )
                .forEach((item) => {
                    if (item !== li && ancestors.indexOf(item) === -1) {
                        H_Tools?.nestedmenu?.deactivateMenuItem(item);
                    }
                });
        }

        /**
         * Event: Handle mouse enter hover
         */
        function menuItemMouseEnter(e) {
            // console.log('Mouse entered menu item');

            const li = e.currentTarget;
            const ul = li.children[1];

            // clear any previously set closing timeouts
            while (hoverTimeouts.length > 0) {
                const timeout = hoverTimeouts.pop();
                clearTimeout(timeout);
            }

            if (disableHoversWhenParentOutOfBounds) {
                if (
                    ul &&
                    ul.classList.contains('-level-1') &&
                    ul.classList.contains('-is-out-of-bounds-top-and-bottom')
                ) {
                    return;
                }

                const parent_ul = li.parentElement;

                if (
                    parent_ul &&
                    parent_ul.classList.contains('-level-1') &&
                    parent_ul.classList.contains(
                        '-is-out-of-bounds-top-and-bottom'
                    )
                ) {
                    return;
                }

                const grandparent_ul = parent_ul.parentElement.parentElement;

                if (
                    grandparent_ul &&
                    grandparent_ul.classList.contains('-level-1') &&
                    grandparent_ul.classList.contains(
                        '-is-out-of-bounds-top-and-bottom'
                    )
                ) {
                    return;
                }
            }

            deactivateOtherItems(li);

            // bail if menu item is already active
            if (ul && ul.classList.contains('-open')) {
                return;
            }

            // activate menu item
            H_Tools?.nestedmenu?.activateMenuItem(menu, li);

            /**
             * Add a cooldown for this menu item.
             *
             * This prevents the caret click listener from immediately deactivating this menu item in
             * two scenarios:
             *
             * 1. When a mouse user intends to click a caret, this function is triggered on mouse hover,
             *    opening the associated submenu. This happens very quickly, so it is likely that the
             *    the user proceeds to click the caret anyway. Without this cooldown, the user's click
             *    would close the submenu, leading to a bad user experience.
             * 2. Some browsers on touch devices, Chrome for example, simulate mouse cursor movements
             *    such that touching a caret triggers the `mouseenter` event in addition to the `click`
             *    event. Without this cooldown, the first click on a caret would result in the submenu
             *    opening and closing immediately. Second touch on the same caret would open the submenu
             *    (since the simulated mouse cursor is already inside the caret). This would be bad UX.
             */
            H_Tools?.nestedmenu?.startCooldown(li);
        }

        /**
         * Event: Handle mouseleave hover
         *
         * Close menu when hover timer has ended (only for horizontal menus)
         */
        function menuItemMouseLeave(e) {
            // console.log('Mouse left menu item');

            if (disableHoversWhenParentOutOfBounds) {
                const parent_ul = e.currentTarget.parentElement;

                if (
                    parent_ul &&
                    parent_ul.classList.contains('-level-1') &&
                    parent_ul.classList.contains(
                        '-is-out-of-bounds-top-and-bottom'
                    )
                ) {
                    return;
                }
            }

            // delay closing for more natural hover
            hoverTimeouts.push(
                setTimeout(
                    function (li) {
                        H_Tools?.nestedmenu?.deactivateMenuItem(li);
                    },
                    750,
                    e.currentTarget
                )
            );
        }

        /**
         * Adjust the position of the sub menu of the supplied menu item.
         *
         * @param li Menu item
         */
        function adjustSubMenuPosition(li) {
            // console.log('Adjusting position of sub menu...');

            const ul = li.children[1];

            if (!ul) {
                return;
            }

            // Remove all modifiers before fetching client rect
            ul.classList.remove('-is-out-of-bounds-right');
            ul.classList.remove('-is-out-of-bounds-bottom');
            ul.classList.remove('-is-out-of-bounds-top-and-bottom');

            const ul_rect = ul.getBoundingClientRect();

            if (ul_rect.width === 0) {
                return;
            }

            // If the submenu overflows the viewport on the right
            if (
                ul_rect.left + ul_rect.width + 10 >
                (window.innerWidth || doc.clientWidth)
            ) {
                ul.classList.add('-is-out-of-bounds-right');
            }

            const li_rect = li.getBoundingClientRect();

            // If the submenu overflows the viewport on the bottom
            if (
                ul_rect.top + ul_rect.height >
                (window.innerHeight || doc.clientHeight)
            ) {
                // If the submenu would not fit on the screen when bottom aligned
                if (li_rect.top + li_rect.height < ul_rect.height) {
                    ul.style.setProperty('--position-y', `${ul_rect.y}px`);
                    ul.classList.add('-is-out-of-bounds-top-and-bottom');
                }

                // If the submenu would fit on the screen when bottom aligned
                else {
                    ul.classList.add('-is-out-of-bounds-bottom');
                }
            }
        }

        function subMenuOpened(e) {
            // console.log('Submenu opened');
            adjustSubMenuPosition(e.currentTarget);
        }

        function viewportResized() {
            // console.log('Viewport resized');

            menu.querySelectorAll('.menu-item-has-children').forEach((item) => {
                adjustSubMenuPosition(item);
            });
        }

        function caretOpened(e) {
            // console.log('Caret opened');

            const li = e.currentTarget;
            const ul = li.parentElement;

            /**
             * When a submenu is taller than the screen ("out of bounds"), the submenu will have a
             * vertical scroll bar. Since the submenu doesn't scroll to the opened submenu in this "out
             * of bounds" -state, it is essential not to close other submenus when others are opened.
             *
             * If we did close other submenus, the user could open a submenu and the opened submenu
             * could be opened off-screen.
             *
             * Preventing this bad UX with this check.
             */
            if (
                ul.classList.contains('-level-1') &&
                ul.classList.contains('-is-out-of-bounds-top-and-bottom')
            ) {
                return;
            }

            deactivateOtherItems(li);
        }

        // setup hooks for menu items
        menu.querySelectorAll('.menu-item').forEach((item) => {
            item.addEventListener('mouseenter', menuItemMouseEnter);
            item.addEventListener('mouseleave', menuItemMouseLeave);
            item.addEventListener('submenuopened', subMenuOpened);
            item.addEventListener('caretopened', caretOpened);
        });

        // update sub menu positions on resize
        window.addEventListener('resize-debounce', viewportResized);

        // close open submenus when the ESC key is pressed
        document.addEventListener('keydown', function (event) {
            if (H_Tools.key.isEscape(event)) {
                menu.querySelectorAll('.menu-item-has-children.-open').forEach(
                    (submenu) => {
                        H_Tools.nestedmenu.deactivateMenuItem(submenu);
                    }
                );
            }
        });
    }

    document.querySelectorAll('.js-menu-nested-horizontal').forEach((menu) => {
        menu_nested_horizontal({
            menu: menu,
        });
    });
})();
